/**
 * Auth source of truth: client id, role names, and how to read roles from the token.
 * All role checks (context, guards, UI) use this module. Backend is authority for enforcement.
 */

/** Keycloak client id for API; roles live in token.resource_access[API_CLIENT_ID].roles */
export const API_CLIENT_ID = 'encardio-calibraone-api';

/** Calibraone role names (from Keycloak group → client role mapping). */
export const ROLES = ['admin', 'subadmin', 'viewer'] as const;
export type AuthRole = (typeof ROLES)[number];

/** User identity from token (roles for UI only). */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  firstName: string;
  lastName: string;
  username: string;
  roles: string[];
  isActive: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

/** Token shape (e.g. keycloak.tokenParsed or decoded JWT). */
export function getRoles(token: Record<string, unknown> | null | undefined): string[] {
  if (!token || typeof token !== 'object') return [];
  const ra = token.resource_access as Record<string, { roles?: string[] }> | undefined;
  const client = ra?.[API_CLIENT_ID];
  const roles = client?.roles;
  return Array.isArray(roles) ? [...roles] : [];
}

export function hasRole(token: Record<string, unknown> | null | undefined, role: string): boolean {
  return getRoles(token).includes(role);
}

export function hasAnyRole(
  token: Record<string, unknown> | null | undefined,
  roles: string[]
): boolean {
  const userRoles = getRoles(token);
  return roles.some((r) => userRoles.includes(r));
}
